// File Location: src/screens/financial/FinancialHistory.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  History,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Building,
  Filter,
  Download,
  Search,
  Eye,
  BarChart3,
  PieChart,
  Activity,
  Target,
  Percent,
  FileText,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import { exportFinancialData } from '../../services/exportService';
import {
  getFinancialProcessedApplications,
  getFinancialPerformanceMetrics
} from '../../services/financialservice';

const FinancialHistory = () => {
  const { userData: user } = useAuth();
  const [applications, setApplications] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedTimeRange, setSelectedTimeRange] = useState('all');

  const [filters, setFilters] = useState({
    status: '',
    searchTerm: '',
    dateFrom: '',
    dateTo: ''
  });

  // Load processed applications
  const loadHistory = useCallback(async (page = 1) => {
    if (!user?.uid) return;

    setLoading(true);
    try {
      const result = await getFinancialProcessedApplications(user.uid, filters, page, 10);
      if (result.success) {
        setApplications(result.applications);
        setTotalPages(result.totalPages);
        setCurrentPage(page);
      }
    } catch (error) {
      console.error('Error loading history:', error);
    } finally {
      setLoading(false);
    }
  }, [user?.uid, filters]);

  // Load performance metrics
  const loadMetrics = useCallback(async () => {
    if (!user?.uid) return;

    try {
      let dateRange = null;
      
      if (selectedTimeRange !== 'all') {
        const endDate = new Date();
        const startDate = new Date();
        
        switch (selectedTimeRange) {
          case 'week':
            startDate.setDate(endDate.getDate() - 7);
            break;
          case 'month':
            startDate.setMonth(endDate.getMonth() - 1);
            break;
          case 'quarter':
            startDate.setMonth(endDate.getMonth() - 3);
            break;
          case 'year':
            startDate.setFullYear(endDate.getFullYear() - 1);
            break;
        }
        
        dateRange = { start: startDate, end: endDate };
      }

      const result = await getFinancialPerformanceMetrics(user.uid, dateRange);
      if (result.success) {
        setMetrics(result.metrics);
      }
    } catch (error) {
      console.error('Error loading metrics:', error);
    }
  }, [user?.uid, selectedTimeRange]);

  useEffect(() => {
    loadHistory(1);
  }, [loadHistory]);

  useEffect(() => {
    loadMetrics();
  }, [loadMetrics]);

  const formatCurrency = (amount) => {
    if (!amount) return '₹0';
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  const formatDate = (date) => {
    if (!date) return '-';
    const dateObj = date.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'approved': { color: 'bg-green-100 text-green-800', text: 'Approved', icon: CheckCircle },
      'rejected': { color: 'bg-red-100 text-red-800', text: 'Rejected', icon: XCircle },
      'pending_info': { color: 'bg-yellow-100 text-yellow-800', text: 'Pending Info', icon: Clock }
    };

    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', text: status, icon: Clock };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </span>
    );
  };

  const handlePageChange = (page) => {
    loadHistory(page);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleExport = () => {
    try {
      const financialData = applications.map(app => ({
        applicationId: app.applicationId,
        patient: app.patient,
        hospitalName: app.hospitalName,
        treatmentCost: app.estimatedCost,
        insuranceCoverage: app.workflow?.financialVerification?.approvedAmount,
        patientContribution: app.workflow?.financialVerification?.patientContribution,
        reimbursementAmount: app.workflow?.financialVerification?.finalDisbursalAmount,
  paymentStatus: app.status === 'financial_approved' ? 'approved' : app.workflow?.financialVerification?.status,
        paymentDate: app.workflow?.financialVerification?.verifiedAt,
        transactionId: app.workflow?.financialVerification?.transactionId,
        invoiceNumber: app.workflow?.financialVerification?.invoiceNumber,
        dischargeDate: app.workflow?.discharge?.completedAt,
        finalSettlement: app.workflow?.financialVerification?.finalSettlementAmount,
        outstandingAmount: app.workflow?.financialVerification?.outstandingAmount
      }));
      
      const result = exportFinancialData(financialData, 'financial_history');
      if (result.success) {
        alert(result.message);
      } else {
        alert('Export failed: ' + result.error);
      }
    } catch (error) {
      console.error('Error exporting financial data:', error);
      alert('Failed to export financial data');
    }
  };

  const PerformanceMetrics = () => {
    if (!metrics) return null;

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Processed</p>
              <p className="text-2xl font-bold text-blue-600">{metrics.totalProcessed}</p>
              <p className="text-xs text-gray-500 mt-1">
                Avg: {metrics.avgDailyProcessing}/day
              </p>
            </div>
            <div className="bg-blue-100 p-3 rounded-lg">
              <Activity className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Approval Rate</p>
              <p className="text-2xl font-bold text-green-600">{metrics.approvalRate}%</p>
              <p className="text-xs text-gray-500 mt-1">
                {metrics.approved} approved
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Processing Time</p>
              <p className="text-2xl font-bold text-purple-600">{metrics.averageProcessingTime}min</p>
              <p className="text-xs text-gray-500 mt-1">
                Total: {Math.round(metrics.totalProcessingTime / 60)}hrs
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <Clock className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Disbursal Efficiency</p>
              <p className="text-2xl font-bold text-teal-600">{metrics.disbursalEfficiency}%</p>
              <p className="text-xs text-gray-500 mt-1">
                {formatCurrency(metrics.totalDisbursedAmount)} disbursed
              </p>
            </div>
            <div className="bg-teal-100 p-3 rounded-lg">
              <Target className="h-6 w-6 text-teal-600" />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ApplicationCard = ({ application }) => {
    const financialData = application.workflow?.financialVerification || {};
    const processingTime = financialData.processingTimeMinutes || 0;
    const disbursalAmount = financialData.finalDisbursalAmount || 0;
    const eligibilityScore = financialData.eligibilityScore || 0;

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-3">
              <h3 className="text-lg font-semibold text-gray-900">{application.applicationId}</h3>
              {getStatusBadge(financialData.status)}
              {application.emergency && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  Emergency
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Patient Info */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <User className="h-4 w-4 mr-2 text-teal-600" />
                  Patient
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><strong>Name:</strong> {application.patient?.name}</div>
                  <div><strong>Hospital:</strong> {application.hospitalName}</div>
                  <div><strong>Policy:</strong> {application.policy?.insuranceCompany}</div>
                </div>
              </div>

              {/* Financial Results */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <DollarSign className="h-4 w-4 mr-2 text-teal-600" />
                  Financial Results
                </h4>
                <div className="text-sm space-y-1">
                  <div className="text-blue-600">
                    <strong>Disbursal:</strong> {formatCurrency(disbursalAmount)}
                  </div>
                  <div className="text-purple-600">
                    <strong>Credit Score:</strong> {financialData.creditScore || 'N/A'}
                  </div>
                  <div className="text-green-600">
                    <strong>Eligibility:</strong> {eligibilityScore}%
                  </div>
                </div>
              </div>

              {/* Processing Details */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <Clock className="h-4 w-4 mr-2 text-teal-600" />
                  Processing
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <div><strong>Time:</strong> {processingTime} minutes</div>
                  <div><strong>Processed:</strong> {formatDate(financialData.verifiedAt)}</div>
                  <div><strong>Risk:</strong> 
                    <span className={`ml-1 capitalize ${
                      financialData.riskAssessment?.overallRisk === 'low' ? 'text-green-600' :
                      financialData.riskAssessment?.overallRisk === 'medium' ? 'text-yellow-600' :
                      'text-red-600'
                    }`}>
                      {financialData.riskAssessment?.overallRisk || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>

              {/* KYC Status */}
              <div className="space-y-2">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <FileText className="h-4 w-4 mr-2 text-teal-600" />
                  KYC Status
                </h4>
                <div className="text-sm text-gray-600 space-y-1">
                  {financialData.kycDetails && (
                    <>
                      <div>
                        <strong>Verified:</strong> {Object.values(financialData.kycDetails).filter(Boolean).length}/8
                      </div>
                      <div>
                        <strong>Lender:</strong> {financialData.lenderDetails?.lenderName || 'N/A'}
                      </div>
                      <div>
                        <strong>Rate:</strong> {financialData.lenderDetails?.interestRate || 'N/A'}%
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Comments */}
            {financialData.comments && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <h5 className="text-sm font-medium text-gray-900 mb-1">Comments:</h5>
                <p className="text-sm text-gray-600">{financialData.comments}</p>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="ml-4">
            <button
              onClick={() => console.log('View details:', application)}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            >
              <Eye className="h-4 w-4" />
              <span>View Details</span>
            </button>
          </div>
        </div>
      </div>
    );
  };

  const FilterBar = () => (
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search applications..."
            value={filters.searchTerm}
            onChange={(e) => handleFilterChange({ ...filters, searchTerm: e.target.value })}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500"
          />
        </div>

        <select
          value={filters.status}
          onChange={(e) => handleFilterChange({ ...filters, status: e.target.value })}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
        >
          <option value="">All Status</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
          <option value="pending_info">Pending Info</option>
        </select>

        <input
          type="date"
          value={filters.dateFrom}
          onChange={(e) => handleFilterChange({ ...filters, dateFrom: e.target.value })}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
          placeholder="From Date"
        />

        <input
          type="date"
          value={filters.dateTo}
          onChange={(e) => handleFilterChange({ ...filters, dateTo: e.target.value })}
          className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
          placeholder="To Date"
        />
      </div>
    </div>
  );

  const Pagination = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow-sm">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing page <span className="font-medium">{currentPage}</span> of{' '}
              <span className="font-medium">{totalPages}</span>
            </p>
          </div>
          <div>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
                if (page > totalPages) return null;
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      page === currentPage
                        ? 'z-10 bg-teal-50 border-teal-500 text-teal-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                );
              })}
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </nav>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 rounded mb-4"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Financial Processing History</h1>
          <p className="text-gray-600">Review your financial verification and KYC processing history</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={selectedTimeRange}
            onChange={(e) => setSelectedTimeRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">All Time</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
            <option value="quarter">Last Quarter</option>
            <option value="year">Last Year</option>
          </select>
          <button
            onClick={handleExport}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Download className="h-4 w-4" />
            <span>Export</span>
          </button>
          <button
            onClick={() => loadHistory(currentPage)}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Performance Metrics */}
      <PerformanceMetrics />

      {/* Performance Summary Cards */}
      {metrics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          {/* Processing Breakdown */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Processing Breakdown</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Approved</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">{metrics.approved}</span>
                  <span className="text-xs text-gray-500 ml-1">
                    ({metrics.totalProcessed > 0 ? Math.round((metrics.approved / metrics.totalProcessed) * 100) : 0}%)
                  </span>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Rejected</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">{metrics.rejected}</span>
                  <span className="text-xs text-gray-500 ml-1">
                    ({metrics.totalProcessed > 0 ? Math.round((metrics.rejected / metrics.totalProcessed) * 100) : 0}%)
                  </span>
                </div>
              </div>

              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                  <span className="text-sm text-gray-600">Pending Info</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-medium text-gray-900">{metrics.pendingInfo}</span>
                  <span className="text-xs text-gray-500 ml-1">
                    ({metrics.totalProcessed > 0 ? Math.round((metrics.pendingInfo / metrics.totalProcessed) * 100) : 0}%)
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Processing Efficiency */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Efficiency Metrics</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Processing Speed</span>
                  <span className="text-sm font-medium text-gray-900">
                    {metrics.averageProcessingTime < 30 ? 'Excellent' : 
                     metrics.averageProcessingTime < 60 ? 'Good' : 'Needs Improvement'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      metrics.averageProcessingTime < 30 ? 'bg-green-500' :
                      metrics.averageProcessingTime < 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ 
                      width: `${Math.min(100, Math.max(10, 100 - metrics.averageProcessingTime))}%` 
                    }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Approval Rate</span>
                  <span className="text-sm font-medium text-gray-900">{metrics.approvalRate}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${
                      metrics.approvalRate >= 80 ? 'bg-green-500' :
                      metrics.approvalRate >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${metrics.approvalRate}%` }}
                  ></div>
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-sm text-gray-600">Daily Processing</span>
                  <span className="text-sm font-medium text-gray-900">{metrics.avgDailyProcessing}/day</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${Math.min(100, (metrics.avgDailyProcessing / 20) * 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Financial Impact */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Financial Impact</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Disbursed</span>
                <span className="text-lg font-bold text-green-600">
                  {formatCurrency(metrics.totalDisbursedAmount)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Claims</span>
                <span className="text-lg font-bold text-blue-600">
                  {formatCurrency(metrics.totalClaimAmount)}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Disbursal Efficiency</span>
                <span className="text-lg font-bold text-purple-600">
                  {metrics.disbursalEfficiency}%
                </span>
              </div>

              <div className="pt-2 border-t border-gray-200">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Emergency Cases</span>
                  <span className="text-sm font-medium text-red-600">
                    {metrics.emergencyProcessed || 0}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filter Bar */}
      <FilterBar />

      {/* Applications History */}
      {applications.length > 0 ? (
        <div className="space-y-4">
          {applications.map((application) => (
            <ApplicationCard key={application.id} application={application} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg shadow-sm">
          <History className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Processing History Found</h3>
          <p className="text-gray-600">
            {Object.values(filters).some(v => v !== '') 
              ? 'Try adjusting your filters to see more history.'
              : 'You haven\'t processed any financial applications yet.'
            }
          </p>
        </div>
      )}

      {/* Pagination */}
      <Pagination />
    </div>
  );
};

export default FinancialHistory;